import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import Media from '@ep/components/Media';
import { Body2, H4 } from '@ep/components/Typography';
import theme from '@ep/theme';
import { Gcms_Card } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import React from 'react';

export interface CardsModuleProps {
  cards: Gcms_Card[];
  themeColor: string;
  addTopGap?: boolean;
}

interface StyledContainerProps {
  addTopGap?: boolean;
  cardsLength: number;
}

const StyledContainer = styled(Container)<StyledContainerProps>`
  display: grid;
  padding-bottom: 7.5rem;
  justify-items: center;
  ${({ addTopGap }) => (addTopGap ? '' : 'padding-top: 7.5rem;')};
  ${({ cardsLength }) =>
    cardsLength > 2 ? 'grid-gap: 1rem; max-width: 1500px;' : 'grid-gap: 2rem;'};
  @media (max-width: ${theme.breakpoints.optimalDesktop}px) {
    grid-template-columns: repeat(2, 1fr) !important;
  }
  @media (max-width: 800px) {
    grid-template-columns: repeat(1, 1fr) !important;
  }
`;

const Image = styled(Media)`
  height: 5rem;
`;

const Card = styled.div`
  background-color: ${theme.colors.lightGray};
  border-top: 0.7rem solid ${({ color }) => color};
  border-radius: 4px;
  padding: 2rem;
  width: 22rem;
  @media (max-width: 380px) {
    width: 15rem;
  }
  display: grid;
`;

const TextContainer = styled.div`
  grid-row: 1;
  align-self: start;
`;

const FeaturesContainer = styled.div`
  grid-row: 2;
  align-self: start;

  ul {
    padding: 0;
    margin: 0;
    li {
      text-align: left;
      list-style: none;
      padding-left: 2rem;
      position: relative;
      &:before {
        content: '>';
        position: absolute;
        left: 0;
        top: 0;
        color: ${theme.colors.oceanBlue};
      }
    }
  }
`;

const Heading = styled(H4.withComponent('h2'))`
  margin: 0;
  padding-bottom: 0.5rem;
  text-align: center;
  color: ${theme.colors.mediumBlue};
  border-bottom: 1px solid ${theme.colors.mediumGray};
`;

const FeaturesHeading = styled(H4.withComponent('h3'))`
  margin: 0;
  text-align: center;
  color: ${theme.colors.mediumBlue};
`;

const Subheading = styled(Body2)`
  margin: 2rem 0;
  text-align: center;
`;

const ButtonContainer = styled.div`
  grid-row: 3;
  margin: 0 auto;
  align-self: end;
`;

function CTACardsModule({ cards, themeColor, addTopGap }: CardsModuleProps) {
  const cardsLength = cards.length;
  const addTopGap2 = addTopGap;
  return (
    <StyledContainer
      addTopGap={addTopGap2}
      cardsLength={cards.length}
      style={{ gridTemplateColumns: 'repeat(' + cardsLength + ', 1fr)' }}
      backgroundColor={
        themeColor === 'page_break' ? theme.colors.mediumBlue : undefined
      }
    >
      {cards.map(card => (
        <Card
          key={card.id}
          color={card.topBorderColor ? card.topBorderColor : 'rgba(0,0,0,0)'}
        >
          <TextContainer>
            <Heading>{card.heading}</Heading>
            {card.subheading && (
              <Subheading {...getHtmlProp(card.subheading.html)} />
            )}
          </TextContainer>
          {card.cardImage && (
            <a target="_blank" href={card.cardImageLink}>
              <Image media={card.cardImage} />
            </a>
          )}
          {card.featuresTitle && (
            <FeaturesContainer>
              <FeaturesHeading>{card.featuresTitle}</FeaturesHeading>
              {card.features && (
                <Subheading {...getHtmlProp(card.features.html)} />
              )}
            </FeaturesContainer>
          )}
          {card.buttonLabel && card.buttonLink && (
            <ButtonContainer>
              <Button
                size="medium"
                label={card.buttonLabel}
                link={card.buttonLink}
              />
            </ButtonContainer>
          )}
        </Card>
      ))}
    </StyledContainer>
  );
}

export default CTACardsModule;
